import { isNavGroup } from '@realtyfeed/app-shell';
import { usePermission } from '@realtyfeed/auth';
import { ROUTES } from '@realtyfeed/routes';
import { LoadingData } from '@realtyfeed/ui';
import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { navigation } from '../config/navigation';

/**
 * Sends a signed-in user to the first screen they can actually open.
 *
 * ### Why this exists at all
 *
 * The console has **no landing page**. `/` and `/dashboard` both redirect; the legacy
 * `components/Dashboard` is routed to nothing and never renders (`docs/04-legacy-inventory.md`
 * §5). Whether that should change is a product question — until it is answered, this reproduces
 * the legacy `RedirectToHomeRoute` behaviour: pick the first nav entry the session may see.
 *
 * ### Why All Users is preferred
 *
 * The legacy version jumped to `/users` when it was reachable and otherwise took the first
 * accessible entry. That is not arbitrary — most staff have `AdminEntitiesUsersPost`, so it
 * gives the majority a stable landing screen instead of one that moves as their permissions
 * change. Kept as-is.
 *
 * ### Waiting, not guessing
 *
 * `isReady` is checked before deciding. Redirecting while the session is unresolved would send
 * every user to whatever the empty-permission case lands on, and then leave them there — the
 * redirect has already happened by the time the real permissions arrive.
 */
export const HomeRedirect = (): ReactNode => {
  const { isReady, canUseAnyPath } = usePermission();

  if (!isReady) return <LoadingData />;

  const reachable = navigation
    .flatMap((item) => (isNavGroup(item) ? item.children : [item]))
    .filter((entry) => entry.to && canUseAnyPath(entry.apiPath ?? []));

  const preferred = reachable.find((entry) => entry.to === ROUTES.users);
  const target = preferred?.to ?? reachable[0]?.to;

  /**
   * A signed-in session that can reach nothing.
   *
   * Possible in practice: a staff account whose role grants only endpoints no sidebar entry
   * cites. Sending them to the error page states the problem rather than looping them back
   * through this component — which is what `<Navigate to="/">` would do.
   */
  if (!target) return <Navigate to={ROUTES.error} replace />;

  return <Navigate to={target} replace />;
};
